import { T, useGT, useLocaleSelector } from 'gt-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguagePreference } from '@/hooks/useLanguagePreference';
import {
  localeDisplayName,
  toRuntimeLocale,
  toUserLocale,
  USER_LOCALES,
} from '@/lib/appLocales';

const RUNTIME_LOCALES = USER_LOCALES.map(toRuntimeLocale);

export function LanguageSettingsPage() {
  const gt = useGT();
  const { locale, setLocale } = useLocaleSelector(RUNTIME_LOCALES);
  const { setLanguage, isLoading, isSaving } = useLanguagePreference();

  const selectLocale = (value: string) => {
    const userLocale = toUserLocale(value);
    if (userLocale === undefined) return;
    setLocale(value);
    setLanguage(userLocale);
  };

  return (
    <div className="p-3">
      <div className="mb-3">
        <p className="text-sm text-muted-foreground">
          <T context="language settings section description">
            Choose the language used throughout Gently. Your choice follows you
            across devices.
          </T>
        </p>
      </div>
      <div>
        {isLoading ? (
          <Skeleton className="h-9 w-64" />
        ) : (
          <div className="space-y-2">
            <Label htmlFor="language-select" className="font-normal">
              <T context="label for the language dropdown">
                Application language
              </T>
            </Label>
            <Select
              value={locale}
              onValueChange={selectLocale}
              disabled={isSaving}
            >
              <SelectTrigger
                id="language-select"
                className="w-64"
                aria-label={gt('Application language', {
                  context: 'aria-label, language settings dropdown',
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {USER_LOCALES.map((userLocale) => (
                  <SelectItem
                    key={userLocale}
                    value={toRuntimeLocale(userLocale)}
                  >
                    {localeDisplayName(userLocale)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
